import { Component } from '@angular/core';
import { Material } from '../../models/models-vm';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StorageService } from '../../../Common/services/storage.service';
import { MasterService } from '../../services/master.service';
import { MessageService } from '../../../project-intake/services/message.service';
import { UserRoleServiceService } from '../../../shared/services/user-role-service.service';
import { ErrorLoggerService } from '../../../shared/services/error-logger.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-master-material',
  templateUrl: './master-material.component.html',
  styleUrls: ['./master-material.component.css', './../../../../assets/css/master_management.css']
  // styleUrl: './master-material.component.css'
})
export class MasterMaterialComponent {
  private LOADER_ID: string = 'admnmaterialmasterloader';

  materialList: Material[] = [];
  filteredMaterials: Material[] = [];
  
  chunkSize = 1000;
  currentChunkIndex = 0;
  materialForm!: FormGroup;
  currentMaterialID?: number | null;
  buttonText = 'Add / Update';

  isActiveOptions = [
    { value: 1, label: 'All' },
    { value: 2, label: 'Yes' },
    { value: 3, label: 'No' },
  ];

  constructor(
    private fb: FormBuilder,
    private storageService: StorageService,
    private masterService: MasterService,
    private msgService: MessageService,
    private userRoleService: UserRoleServiceService,
    private errorLogService: ErrorLoggerService,
    private ngxService: NgxUiLoaderService
  ) {}

  async ngOnInit() {
    this.initForm();
    await this.loadInitialData();
    
    this.materialForm.get('searchControl')?.valueChanges.pipe(
      debounceTime(300), // Wait 300ms after the last keystroke
      distinctUntilChanged() // Only emit if the value has changed
    ).subscribe((searchTerm) => {
      this.filterMaterials(searchTerm);
    }); 
  }

  private initForm(): void {
    this.materialForm = this.fb.group({
      materialID: [null],
      materialName: ['', Validators.required],
      materialType: ['', Validators.required],
      dupMaterialType: [''],
      isActive: [true, [Validators.required]],
      isActiveFilter: [1],
      searchControl: [''],
    });
  }

  private async loadInitialData() {
    this.ngxService.start(this.LOADER_ID);
    this.materialList = [];
    await this.onApplyFilter();
  }

  loadNextChunk() {
    const nextChunk = this.getMaterialChunk(this.currentChunkIndex, this.chunkSize);
    this.filteredMaterials = [...this.filteredMaterials, ...nextChunk];
    this.currentChunkIndex++;
  }

  getMaterialChunk(chunkIndex: number, chunkSize: number): Material[] {
    const start = chunkIndex * chunkSize;
    const end = start + chunkSize;
    return this.materialList.slice(start, end);
  }

  onScroll() {
    if (this.currentChunkIndex * this.chunkSize < this.materialList.length) {
      this.loadNextChunk();
    }
  }

  private filterMaterials(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredMaterials = [];
      this.filteredMaterials = this.materialList;
      this.resetFormFields();
      return;
    }
    const search = searchTerm.toLowerCase();
    this.filteredMaterials = this.materialList.filter(
      (material) =>
          material.materialName?.toLowerCase().includes(search) ||
          material.materialType?.toLowerCase().includes(search)
      );
    this.resetFormFields();
  }

  enableOrDisableMaterial(event: any, material: Material): void {
    material.isActive = event.target.checked;
    this.updateMaterial(material, 'Material updated successfully');
  }

  private updateMaterial(material: Material, successMessage: string): void {
    this.ngxService.start(this.LOADER_ID);
    this.masterService.updateMaterial(material).subscribe({
      next: () => {
        this.msgService.htmlSuccess(successMessage);
        this.resetSearchAndApplyFilter();
      },
      error: () => this.msgService.htmlError('Failed to update Material'),
      complete: () => {
        //this.ngxService.stop(this.LOADER_ID)
      },
    });
  }

  private async resetSearchAndApplyFilter() {
    this.materialForm.get('searchControl')?.setValue('');
    await this.onApplyFilter();
  }

  private handleValidationErrors(): void {
    const keyMap: any = {
      materialName: 'Material Name',
      materialType: 'Material Type',
      isActive: 'Is Active'
    };   

    const errors = Object.keys(this.materialForm.controls).reduce((acc, key) => {
      const controlErrors = this.materialForm.get(key)?.errors;
      if (controlErrors) {
        if (controlErrors['required']) acc.push(`${keyMap[key]} is required.`);
        if (controlErrors['pattern']) acc.push(`${key} must be a valid number.`);
      }
      return acc;
    }, [] as string[]);

    if (errors.length) {
      this.msgService.htmlError(errors.join('<br/>'));
    }
  }

  onSubmit()
  {
    if (this.materialForm.invalid) {
      this.handleValidationErrors();
      return;
    }

    const material: Material = this.materialForm.value;

    this.ngxService.start(this.LOADER_ID);

    this.errorLogService.logMessage(material, ' Before Save');

    if (this.currentMaterialID) {
      this.masterService.updateMaterial(material).subscribe({
        next: (result) => {
          if(result == 3)
          {
            this.msgService.htmlInfo('A Material with same MaterialType is already exists.');
            this.ngxService.stop(this.LOADER_ID);
          }
          else
          {
            this.msgService.htmlSuccess('Material updated successfully');
            this.resetForm();
            this.onApplyFilter();
          }
        },
        error: (err) => this.handleError('Failed to update store', err),
        complete: () => {
          //this.ngxService.stop(this.LOADER_ID)
        },
      });
    } else {
      this.masterService.addMaterial(this.prepareNewMaterial(material)).subscribe({
        next: (result) => {

          if(result == 3)
          {
            this.msgService.htmlInfo('A Material with same MaterialType is already exists.');
            this.ngxService.stop(this.LOADER_ID);
          }
          else
          {
            this.msgService.htmlSuccess('Material added successfully');
            this.resetForm();
            this.onApplyFilter();
          }
        },
        error: (err) => this.handleError('Failed to add store', err),
        complete: () => {
          //this.ngxService.stop(this.LOADER_ID)
        },
      });
    }
  }

  private prepareNewMaterial(material: Material): Material {
    return {
      ...material,
      // isActive: true,
      // isVerified: true,
      // parentID: 0,
    };
  }

  editMaterial(materialId: number): void {
    this.currentMaterialID = materialId;
    const store = this.materialList.find((p) => p.materialID === materialId);
    if (store) {
      this.materialForm.patchValue(store);
      let abc = this.materialForm.value;

      this.buttonText = 'Add / Update';
    }
  }
  
  resetFormFields()
  {
    this.materialForm.get('materialID')?.setValue(null);
    this.materialForm.get('materialName')?.setValue('');
    this.materialForm.get('materialType')?.setValue('');
    this.materialForm.get('isActive')?.setValue(true);
  }

  resetForm(): void {
    this.currentMaterialID = null;
    this.materialForm.reset({
      isActive: true,
      isActiveFilter: 1,
    });
    this.buttonText = 'Add / Update';
  }

  async onApplyFilter(isFilterClicked: boolean = false) {
    this.materialForm.get('searchControl')?.setValue('');
    this.materialList = [];
    this.filteredMaterials =[];

    const { vendorIDFilter, isActiveFilter, isVerifiedFilter } = this.materialForm.value;
    
    if(isFilterClicked)
    {
      this.resetFormFields();
      this.ngxService.start(this.LOADER_ID);
    }

    this.masterService.getAllMaterials(isActiveFilter).subscribe({
      next: (result) => {
        this.materialList = result;
        this.currentChunkIndex = 0
        this.loadNextChunk();
        //console.log('storesList', this.storesList);
      },
      error: (error) => this.msgService.htmlError('Error occurred while fetching store details.'),
      complete: () => {
        this.ngxService.stop(this.LOADER_ID);
      },
    });
  }

  ngOnDestroy() {
    this.materialList = [];
  }

  private handleError(message: string, error: any): void {
    this.msgService.htmlError(message);
    this.errorLogService.logErrorNew(message, error);
    //this.ngxService.stop();
  }
}
